import type { PlayerStats, PlayerStatsSnapshot } from '../gameplay/player';

/**
 * Submersion overlay + oxygen bar. Two pieces of UI in one package:
 *
 *   1. A full-screen blue tint that pulses softly while the player's head
 *      is below the water surface, with a few drifting bubble dots so the
 *      effect reads as "you're submerged" rather than "screen broke".
 *   2. An oxygen bar pinned to the bottom-centre of the screen, only
 *      visible while underwater. The bar reflects the oxygen ratio from
 *      `PlayerStats`; the gameplay system in `GameDirector` is the one
 *      consuming/refilling the value.
 *
 * The class deliberately exposes a tiny imperative API (`setUnderwater`,
 * dispose) — the GameDirector decides when to show/hide; the UI doesn't
 * sample world state.
 */
export class UnderwaterOverlay {
  readonly element: HTMLElement;
  private readonly oxygenBar: HTMLElement;
  private readonly oxygenFill: HTMLElement;
  private readonly oxygenValue: HTMLElement;
  private readonly tint: HTMLElement;
  private readonly bubbles: HTMLElement;
  private readonly unsubscribe: () => void;
  private underwater = false;

  constructor(stats: PlayerStats) {
    this.element = document.createElement('div');
    this.element.className = 'underwater-overlay';

    // Blue tint layer + caustics-ish soft gradient.
    this.tint = document.createElement('div');
    this.tint.className = 'underwater-tint';
    this.element.appendChild(this.tint);

    // Drifting bubbles container.
    this.bubbles = document.createElement('div');
    this.bubbles.className = 'underwater-bubbles';
    for (let i = 0; i < 14; i++) {
      const bubble = document.createElement('span');
      bubble.className = 'underwater-bubble';
      bubble.style.left = `${Math.random() * 100}%`;
      bubble.style.animationDelay = `${(Math.random() * -8).toFixed(2)}s`;
      bubble.style.animationDuration = `${(4 + Math.random() * 5).toFixed(2)}s`;
      bubble.style.transform = `scale(${(0.6 + Math.random() * 0.9).toFixed(2)})`;
      this.bubbles.appendChild(bubble);
    }
    this.element.appendChild(this.bubbles);

    // Oxygen bar — bottom centre.
    this.oxygenBar = document.createElement('div');
    this.oxygenBar.className = 'underwater-oxygen';

    const label = document.createElement('span');
    label.className = 'underwater-oxygen-label';
    label.textContent = 'O₂';

    const barWrap = document.createElement('div');
    barWrap.className = 'underwater-oxygen-bar';

    this.oxygenFill = document.createElement('div');
    this.oxygenFill.className = 'underwater-oxygen-fill';
    barWrap.appendChild(this.oxygenFill);

    this.oxygenValue = document.createElement('span');
    this.oxygenValue.className = 'underwater-oxygen-value';
    this.oxygenValue.textContent = '100';

    this.oxygenBar.append(label, barWrap, this.oxygenValue);
    this.element.appendChild(this.oxygenBar);

    this.unsubscribe = stats.subscribe((snap) => this.apply(snap));
  }

  /** Toggle the underwater state — controls visibility of both the tint
   *  and the oxygen bar. */
  setUnderwater(underwater: boolean): void {
    if (this.underwater === underwater) return;
    this.underwater = underwater;
    this.element.classList.toggle('is-underwater', underwater);
  }

  isUnderwater(): boolean {
    return this.underwater;
  }

  dispose(): void {
    this.unsubscribe();
    this.element.remove();
  }

  private apply(snap: PlayerStatsSnapshot): void {
    const ratio =
      snap.oxygenMax > 0
        ? Math.max(0, Math.min(1, snap.oxygen / snap.oxygenMax))
        : 0;
    const widthStr = `${(ratio * 100).toFixed(1)}%`;
    if (this.oxygenFill.dataset['lastWidth'] !== widthStr) {
      this.oxygenFill.style.width = widthStr;
      this.oxygenFill.dataset['lastWidth'] = widthStr;
    }
    const valueStr = `${Math.round(snap.oxygen)}`;
    if (this.oxygenValue.textContent !== valueStr) {
      this.oxygenValue.textContent = valueStr;
    }
    this.oxygenFill.classList.toggle('is-low', ratio < 0.3);
    this.oxygenFill.classList.toggle('is-critical', ratio < 0.12);
  }
}
